import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  ClockIcon, ExclamationTriangleIcon, CheckCircleIcon,
  Cog6ToothIcon, UserCircleIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const formatRole = (r) => ({ staff: 'Childcare Staff', lead_admin: 'Lead Admin', cook: 'Cook', director: 'Director' }[r] || r);

const eventTypeLabels = {
  late_arrival: 'Late arrival',
  late_return_from_break: 'Late from break',
  call_off_advance: 'Called off (advance)',
  call_off_short_notice: 'Called off (short notice)',
  call_off_holiday: 'Called off (holiday adj.)',
  missing_partial_shift: 'Missed partial shift',
  no_show: 'No call no show',
  missed_meeting: 'Missed staff meeting',
  excused_doctor_note: 'Excused (doctor)',
  manual_adjustment: 'Manual adjustment',
};

const FILTERS = [
  { id: 'all', label: 'All Staff' },
  { id: 'flagged', label: 'At Threshold' },
  { id: 'probation_90', label: 'On 90-day Probation' },
  { id: 'attendance_probation', label: 'On Attendance Probation' },
];

export default function AttendancePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/attendance/dashboard');
      setRows(data);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  };

  const visible = rows.filter(r => {
    if (filter === 'flagged') return !!r.threshold;
    if (filter === 'probation_90') return r.on_probation;
    if (filter === 'attendance_probation') return r.on_attendance_probation;
    return true;
  });

  const stats = {
    flagged: rows.filter(r => !!r.threshold).length,
    probation: rows.filter(r => r.on_probation).length,
    attendanceProbation: rows.filter(r => r.on_attendance_probation).length,
  };

  return (
    <div className="animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance & Points</h1>
          <p className="text-gray-500 text-sm">{rows.length} active staff</p>
        </div>
        <Link to="/attendance/rules"
          className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 text-sm font-medium">
          <Cog6ToothIcon className="w-4 h-4" /> Rules & Thresholds
        </Link>
      </div>

      {/* Stat cards — each filters the table below to that subset */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Active Staff"
          value={rows.length}
          icon={UserCircleIcon}
          color="gray"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <StatCard
          label="At Threshold"
          value={stats.flagged}
          icon={ExclamationTriangleIcon}
          color="red"
          active={filter === 'flagged'}
          onClick={() => setFilter('flagged')}
        />
        <StatCard
          label="On 90-day Probation"
          value={stats.probation}
          icon={ClockIcon}
          color="amber"
          active={filter === 'probation_90'}
          onClick={() => setFilter('probation_90')}
        />
        <StatCard
          label="On Attendance Probation"
          value={stats.attendanceProbation}
          icon={ExclamationTriangleIcon}
          color="amber"
          active={filter === 'attendance_probation'}
          onClick={() => setFilter('attendance_probation')}
        />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No staff match this filter</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="px-6 py-3">Staff</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Points</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Last Event</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <Link to={`/staff/${s.id}`} className="flex items-center gap-3">
                      {s.photo_url ? (
                        <img src={s.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 font-semibold">
                          {(s.first_name?.[0] || '') + (s.last_name?.[0] || '')}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{s.first_name} {s.last_name}</p>
                        <p className="text-xs text-gray-500">{formatRole(s.role)}</p>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">
                    {s.employment_type === 'part_time' ? 'Part-time' : 'Full-time'}
                  </td>
                  <td className="px-6 py-3">
                    <PointsBadge balance={s.balance} threshold={s.threshold} />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1">
                      {s.on_probation && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">90-day Probation</span>
                      )}
                      {s.on_attendance_probation && (
                        <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">Attendance Probation</span>
                      )}
                      {s.threshold && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          s.threshold.color === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {s.threshold.action_label}
                        </span>
                      )}
                      {!s.threshold && !s.on_probation && !s.on_attendance_probation && (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">
                    {s.last_event ? (
                      <div>
                        <p className="text-xs">{eventTypeLabels[s.last_event.event_type] || s.last_event.event_type}</p>
                        <p className="text-xs text-gray-400">{new Date(s.last_event.occurred_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</p>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Link to={`/staff/${s.id}`} className="text-gray-400 hover:text-orange-500">
                      <ChevronRightIcon className="w-5 h-5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, active, onClick }) {
  const colors = {
    gray: 'bg-gray-50 text-gray-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  const ringColors = {
    gray: 'ring-gray-400',
    red: 'ring-red-400',
    amber: 'ring-amber-400',
  };
  const body = (
    <>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-4 w-full text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${active ? `ring-2 ${ringColors[color]}` : ''}`}
      >
        {body}
      </button>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-4">
      {body}
    </div>
  );
}

function PointsBadge({ balance, threshold }) {
  const color = threshold?.color === 'red' ? 'red' : threshold ? 'amber' : balance > 0 ? 'gray' : 'green';
  const styles = {
    green: 'bg-green-50 text-green-700',
    gray: 'bg-gray-50 text-gray-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-semibold ${styles[color]}`}>
      {balance === 0 ? <CheckCircleIcon className="w-4 h-4" /> : null}
      {Number(balance).toFixed(1)} pts
    </span>
  );
}
